import {renderToString} from 'react-dom/server';
import {stripHtml} from 'string-strip-html';
import type {ColumnFilter, FilterCondition, RelativeDateValue} from '@/@types/editableTable';
import type {EditableTablePropertyEntry, EditableTableSortEntry} from '@/ui/EditableTable/EditableTable';

export type ExportFn<T> = (item: T, label: string, exporting?: boolean) => unknown;

/** Inclusive [start, end] window of a Date range, both at day precision. */
type DateWindow = { start: Date, end: Date };

/** Stringify a single primitive / ReactNode value into plain text. */
function stringifyOne(v: unknown): string {
    if (v === null || v === undefined) return '';
    if (typeof v === 'object' && !(v instanceof Date)) {
        return stripHtml(renderToString(v as any)).result;
    }
    return String(v);
}

/**
 * Coerce an arbitrary value into a primitive form suitable for sort / filter / Excel export.
 * Arrays get per-element stringified and comma-joined (so a Team column with
 * [<span>JMC</span>, <span>MR</span>] becomes "JMC, MR" rather than "JMCMR" from a naive
 * renderToString). Non-Date objects (ReactNodes) get stripped to plain text. Primitives,
 * numbers, Dates, null and undefined pass through untouched.
 *
 * Used by EditableTable's Excel export loop to avoid duplicating extractValue's array logic.
 */
export function flattenForCell(raw: unknown): string | number | Date | null | undefined {
    if (Array.isArray(raw)) {
        return raw
            .map(stringifyOne)
            .filter(s => s.length > 0)
            .join(', ');
    }
    if (typeof raw === 'object' && raw !== null && !(raw instanceof Date)) {
        return stripHtml(renderToString(raw as any)).result;
    }
    return raw as string | number | Date | null | undefined;
}

/**
 * Coerce a raw cell value (which may be a ReactNode, primitive, or rendered HTML)
 * into a primitive comparable form. Returns the same value type the existing
 * criteriaMet() worked with, so we can drop into operators below.
 *
 * Exported so callers (e.g., EditableTable's checklist auto-derive) can produce
 * consistent string values without duplicating the array / ReactNode handling.
 */
export function extractValue<T>(
    item: T,
    column: EditableTablePropertyEntry<T>,
    exportFn?: ExportFn<T>,
): unknown {
    const fn = exportFn || column?.renderItemFn;
    const value: any = fn ? fn(item, column.label, true) : (item as any)[column.label];

    // Arrays of React elements (e.g., the Team column's per-user badges) need to be
    // rendered per-element and joined with a separator, otherwise the rendered HTML
    // for adjacent elements concatenates with no delimiter (e.g. "JMC MR" instead of "JMC, MR").
    if (Array.isArray(value)) {
        return value
            .map(stringifyOne)
            .filter(s => s.length > 0)
            .join(', ');
    }

    // Date instances are passed through as-is — toDate() handles them downstream.
    // ReactNode-shaped objects get stripped to plain text via renderToString.
    if (typeof value === 'object' && value !== null && !(value instanceof Date)) {
        const stripped = stripHtml(renderToString(value)).result;
        return stripped || renderToString(value) || '';
    }
    return value;
}

/**
 * Like extractValue, but for multi-value cells returns each element as its own string.
 * For non-array cells, returns a single-element list (or [] if blank).
 *
 * This is the canonical view for the "Limit to values" checklist: a row with `[A, B]`
 * is treated as participating in BOTH "A" and "B", so checking "A" matches rows
 * `[A]` and `[A, B]` and `[A, C]` alike. It's also what the predicate's checklist
 * branch evaluates against — any element matching any selected value is a match.
 */
export function extractValueParts<T>(
    item: T,
    column: EditableTablePropertyEntry<T>,
    exportFn?: ExportFn<T>,
): string[] {
    const fn = exportFn || column?.renderItemFn;
    const value: any = fn ? fn(item, column.label, true) : (item as any)[column.label];

    if (Array.isArray(value)) {
        // Multi-value: drop blanks. A row whose array is empty (or all-blank) has no values
        // to contribute to the checklist; it lives on the operator side via is-empty.
        return value
            .map(stringifyOne)
            .filter(s => s.length > 0);
    }

    // Atomic scalar / ReactNode → single element, including empty string. Blank atomic
    // cells are a meaningful value ("Status was never set"), so they participate in the
    // checklist as a single "" entry that the UI renders as "(blank)".
    return [stringifyOne(value)];
}

const isEmpty = (value: unknown): boolean => value === null || value === undefined || value === '';

const stripMoneySpaces = (s: string): string => s.replace(/\s+/g, '');

function toNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    const cleaned = stripMoneySpaces(String(value)).replace(/[$,]/g, '');
    const n = Number.parseFloat(cleaned);
    return Number.isFinite(n) ? n : null;
}

function toDate(value: unknown): Date | null {
    if (value === null || value === undefined || value === '') return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : startOfDay(value);
    const d = new Date(String(value));
    return Number.isNaN(d.getTime()) ? null : startOfDay(d);
}

function startOfDay(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function endOfDay(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
}

function addDays(d: Date, n: number): Date {
    const r = new Date(d);
    r.setDate(r.getDate() + n);
    return r;
}

/** ISO week starting on Monday — matches how date-fns and most calendars present it. */
function startOfWeek(d: Date): Date {
    const day = d.getDay();
    const offset = day === 0 ? -6 : 1 - day;
    return addDays(startOfDay(d), offset);
}

/** Computes the inclusive day-precision window that a relative date keyword resolves to today. */
export function relativeDateRange(rel: RelativeDateValue, now: Date = new Date()): DateWindow {
    const today = startOfDay(now);
    switch (rel) {
        case 'today':       return {start: today,                   end: endOfDay(today)};
        case 'yesterday':   return {start: addDays(today, -1),      end: endOfDay(addDays(today, -1))};
        case 'tomorrow':    return {start: addDays(today, 1),       end: endOfDay(addDays(today, 1))};
        case 'this_week': {
            const s = startOfWeek(today);
            return {start: s, end: endOfDay(addDays(s, 6))};
        }
        case 'last_week': {
            const s = addDays(startOfWeek(today), -7);
            return {start: s, end: endOfDay(addDays(s, 6))};
        }
        case 'next_week': {
            const s = addDays(startOfWeek(today), 7);
            return {start: s, end: endOfDay(addDays(s, 6))};
        }
        case 'this_month': {
            const s = new Date(today.getFullYear(), today.getMonth(), 1);
            const e = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59, 999);
            return {start: s, end: e};
        }
        case 'last_month': {
            const s = new Date(today.getFullYear(), today.getMonth() - 1, 1);
            const e = new Date(today.getFullYear(), today.getMonth(), 0, 23, 59, 59, 999);
            return {start: s, end: e};
        }
        case 'next_month': {
            const s = new Date(today.getFullYear(), today.getMonth() + 1, 1);
            const e = new Date(today.getFullYear(), today.getMonth() + 2, 0, 23, 59, 59, 999);
            return {start: s, end: e};
        }
        case 'this_year': {
            const s = new Date(today.getFullYear(), 0, 1);
            const e = new Date(today.getFullYear(), 11, 31, 23, 59, 59, 999);
            return {start: s, end: e};
        }
        case 'last_year': {
            const s = new Date(today.getFullYear() - 1, 0, 1);
            const e = new Date(today.getFullYear() - 1, 11, 31, 23, 59, 59, 999);
            return {start: s, end: e};
        }
        case 'next_year': {
            const s = new Date(today.getFullYear() + 1, 0, 1);
            const e = new Date(today.getFullYear() + 1, 11, 31, 23, 59, 59, 999);
            return {start: s, end: e};
        }
    }
}

function ytdRange(now: Date = new Date()): DateWindow {
    const today = endOfDay(now);
    const start = new Date(today.getFullYear(), 0, 1);
    return {start, end: today};
}

type ColumnKind = 'string' | 'number' | 'money' | 'date' | 'boolean' | 'other';

function resolveKind<T>(sortProp: EditableTableSortEntry<T> | undefined, column: EditableTablePropertyEntry<T>): ColumnKind {
    return (sortProp?.type || column?.type || 'other') as ColumnKind;
}

function evaluateText(rawValue: unknown, condition: FilterCondition, opts?: {caseSensitive?: boolean, wholeWords?: boolean}): boolean {
    const cs = !!opts?.caseSensitive;
    const ww = !!opts?.wholeWords;
    const value = isEmpty(rawValue) ? '' : String(rawValue);
    const target = condition.value === undefined || condition.value === null ? '' : String(condition.value);
    const a = cs ? value : value.toLowerCase();
    const b = cs ? target : target.toLowerCase();

    const wordContains = (haystack: string, needle: string): boolean => {
        if (!needle) return true;
        // The needle is escaped via escapeRegex(), so pattern injection is impossible.
        // eslint-disable-next-line security/detect-non-literal-regexp
        const re = new RegExp(`(?:^|\\W)${escapeRegex(needle)}(?:$|\\W)`, cs ? '' : 'i');
        return re.test(haystack);
    };

    switch (condition.op) {
        case 'contains':     return ww ? wordContains(value, target) : a.includes(b);
        case 'not_contains': return ww ? !wordContains(value, target) : !a.includes(b);
        case 'equals':       return a === b;
        case 'not_equals':   return a !== b;
        case 'begins':       return a.startsWith(b);
        case 'ends':         return a.endsWith(b);
        case 'empty':        return isEmpty(rawValue) || a === '';
        case 'not_empty':    return !isEmpty(rawValue) && a !== '';
        default:             return true;
    }
}

function escapeRegex(s: string): string {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function evaluateNumber(rawValue: unknown, condition: FilterCondition): boolean {
    const v = toNumber(rawValue);

    switch (condition.op) {
        case 'empty':       return v === null;
        case 'not_empty':   return v !== null;
    }
    if (v === null) return false;

    const a = toNumber(condition.value);
    const b = toNumber(condition.value2);
    switch (condition.op) {
        case 'equals':      return a !== null && v === a;
        case 'not_equals':  return a !== null && v !== a;
        case 'gt':          return a !== null && v > a;
        case 'gte':         return a !== null && v >= a;
        case 'lt':          return a !== null && v < a;
        case 'lte':         return a !== null && v <= a;
        case 'between':     return a !== null && b !== null && v >= Math.min(a, b) && v <= Math.max(a, b);
        case 'top_n':
        case 'bottom_n':    return true; // resolved post-sort, see applyRankFilters()
        default:            return true;
    }
}

function evaluateDate(rawValue: unknown, condition: FilterCondition, now: Date): boolean {
    const v = toDate(rawValue);

    switch (condition.op) {
        case 'empty':       return v === null;
        case 'not_empty':   return v !== null;
    }
    if (v === null) return false;

    if (condition.op === 'relative') {
        const rel = condition.value as RelativeDateValue | undefined;
        if (!rel) return true;
        const range = relativeDateRange(rel, now);
        return v >= range.start && v <= range.end;
    }
    if (condition.op === 'ytd') {
        const range = ytdRange(now);
        return v >= range.start && v <= range.end;
    }

    const a = toDate(condition.value);
    const b = toDate(condition.value2);
    switch (condition.op) {
        case 'on':          return a !== null && v.getTime() === a.getTime();
        case 'before':      return a !== null && v < a;
        case 'after':       return a !== null && v > a;
        case 'between': {
            if (a === null || b === null) return false;
            const lo = a <= b ? a : b;
            const hi = a <= b ? b : a;
            return v >= lo && v <= endOfDay(hi);
        }
        default:            return true;
    }
}

function evaluateBoolean(rawValue: unknown, condition: FilterCondition): boolean {
    const truthy = rawValue === true || rawValue === 'true' || rawValue === 1 || rawValue === '1';
    const falsey = rawValue === false || rawValue === 'false' || rawValue === 0 || rawValue === '0';
    switch (condition.op) {
        case 'equals': {
            const v = String(condition.value ?? '').toLowerCase();
            if (v === 'true') return truthy;
            if (v === 'false') return falsey;
            return false;
        }
        case 'empty':       return !truthy && !falsey;
        case 'not_empty':   return truthy || falsey;
        default:            return true;
    }
}

/**
 * Evaluate a single condition against an item's raw cell value.
 * Returns true if the row passes; rank-based ops (top_n / bottom_n) always pass here
 * and are resolved post-sort in applyRankFilters().
 */
export function evaluateCondition<T>(
    rawValue: unknown,
    condition: FilterCondition,
    kind: ColumnKind,
    textOptions?: FilterTextOptionsAlias,
    now: Date = new Date(),
): boolean {
    // empty / not_empty are dispatched to the type-specific evaluator so that NaN counts as
    // empty for a number column, an unparseable date string counts as empty for a date column,
    // and so on. The text evaluator falls back to a string-level isEmpty().
    switch (kind) {
        case 'string':
        case 'other':
            return evaluateText(rawValue, condition, textOptions);
        case 'number':
        case 'money':
            return evaluateNumber(rawValue, condition);
        case 'date':
            return evaluateDate(rawValue, condition, now);
        case 'boolean':
            return evaluateBoolean(rawValue, condition);
    }
}

// Local alias to avoid circular import when a future test wants to spy on options.
type FilterTextOptionsAlias = {caseSensitive?: boolean, wholeWords?: boolean};

/**
 * Evaluate a complete column filter against an item.
 * Cross-column ANDing is the caller's responsibility.
 */
export function evaluateColumnFilter<T>(
    item: T,
    filter: ColumnFilter,
    column: EditableTablePropertyEntry<T>,
    sortProp: EditableTableSortEntry<T> | undefined,
    exportFn?: ExportFn<T>,
    now: Date = new Date(),
): boolean {
    const kind = resolveKind(sortProp, column);

    if (kind === 'boolean' && filter.boolValue && filter.boolValue !== 'either') {
        const condition: FilterCondition = {op: 'equals', value: filter.boolValue};
        const rawValue = extractValue(item, column, exportFn);
        return evaluateBoolean(rawValue, condition);
    }

    const rawValue = extractValue(item, column, exportFn);

    if (filter.checklist && filter.checklist.length > 0) {
        const cs = !!filter.textOptions?.caseSensitive;
        // Multi-value (array) cells participate in the checklist per-element. checklistJoin
        // controls how multiple checked values combine:
        //   'or'  (default) — row matches if ANY of its parts is in the checklist.
        //   'and'           — row matches only if every checked value is in its parts.
        // For atomic cells the two are equivalent (parts has at most one value), so the
        // dialog hides the toggle on atomic columns.
        const parts = extractValueParts(item, column, exportFn);
        const partsLc = cs ? new Set(parts) : new Set(parts.map(p => p.toLowerCase()));
        const selectedLc = cs ? filter.checklist : filter.checklist.map(c => c.toLowerCase());

        const matches = filter.checklistJoin === 'and'
            ? selectedLc.every(v => partsLc.has(v))
            : selectedLc.some(v => partsLc.has(v));
        if (!matches) return false;
    }

    const conditions = filter.conditions ?? [];
    if (conditions.length === 0) return true;

    const join = filter.join ?? 'and';
    const results = conditions.map(c => evaluateCondition(rawValue, c, kind, filter.textOptions, now));
    return join === 'or' ? results.some(Boolean) : results.every(Boolean);
}

/**
 * Apply rank-based filters (top_n / bottom_n) to an already-sorted item array.
 * Each top/bottom condition is tied to a column; we collect the union/intersection
 * via per-column sort. For now we apply them in declaration order, slicing the
 * current array — the user-set sort still wins for the visible order, but each
 * top_n / bottom_n re-sorts internally on its own column to compute the slice.
 */
export function applyRankFilters<T>(
    items: T[],
    filters: ColumnFilter[],
    columns: EditableTablePropertyEntry<T>[],
    sortProps: EditableTableSortEntry<T>[],
    exportFns: Map<string, ExportFn<T> | undefined>,
): T[] {
    let result = items;
    for (const filter of filters) {
        for (const condition of filter.conditions ?? []) {
            if (condition.op !== 'top_n' && condition.op !== 'bottom_n') continue;
            const n = Number.parseInt(String(condition.value ?? ''), 10);
            if (!Number.isFinite(n) || n <= 0) continue;
            const column = columns.find(c => c.label === filter.label);
            if (!column) continue;
            const sortProp = sortProps.find(sp => sp.label === filter.label);
            const exportFn = exportFns.get(filter.label);

            const ranked = [...result].sort((a, b) => {
                const va = toNumber(extractValue(a, column, exportFn));
                const vb = toNumber(extractValue(b, column, exportFn));
                if (va === null && vb === null) return 0;
                if (va === null) return 1;
                if (vb === null) return -1;
                return condition.op === 'top_n' ? vb - va : va - vb;
            });
            const keep = new Set(ranked.slice(0, n));
            result = result.filter(item => keep.has(item));
        }
    }
    return result;
}

/** Convenience: does any condition in this filter need post-sort rank handling? */
export function hasRankFilter(filter: ColumnFilter): boolean {
    return (filter.conditions ?? []).some(c => c.op === 'top_n' || c.op === 'bottom_n');
}
